import { LAYOUT } from '@konvoi/shared';

/**
 * Die Höhe des Sichtfelds — selbst gemessen statt dem Browser geglaubt.
 *
 * ## Warum das nötig ist
 *
 * Die Hülle hing bisher an `100dvh`. Die Einheit soll das *jetzige* Sichtfeld
 * messen, und auf dem Entwicklungsrechner tut sie das auch. Auf dem iPhone
 * bleibt sie nach einer Drehung gelegentlich auf dem alten Wert stehen: Unten
 * blieb eine weiße Fläche, und oben verschwanden Zustandsleiste und
 * Fahrtwerte aus dem Bild (beobachtet am 14.08.2026, wieder am 18.08.2026).
 *
 * A-31 hat dagegen bereits das Scrollen abgestellt — seither liegen wenigstens
 * die Trefferflächen richtig. Geblieben war die falsche Höhe.
 *
 * ## Was hier geschieht
 *
 * `visualViewport` meldet, was **tatsächlich zu sehen ist**, und meldet jede
 * Änderung. Der gemessene Wert steht als `--sichtfeld` am Wurzelknoten; die CSS
 * benutzt ihn und behält `100dvh` als Rückfall, falls dieses Modul nie läuft.
 *
 * Zweimal gemessen wird nach einer Drehung: `orientationchange` meldet iOS,
 * **bevor** die neuen Maße feststehen — siehe LAYOUT.orientationSettleMs.
 *
 * Bleibt das Dokument doch einmal verschoben stehen, wird es zurückgeholt.
 * Kosten tut das nichts: Was ohnehin auf null steht, wird nicht angefasst.
 */
export function beobachteSichtfeld(): () => void {
  const messen = (): void => {
    const sicht = window.visualViewport;
    const höhe = Math.round(sicht?.height ?? window.innerHeight);

    if (höhe > 0) {
      document.documentElement.style.setProperty('--sichtfeld', `${String(höhe)}px`);
    }

    if (window.scrollY !== 0 || window.scrollX !== 0) {
      window.scrollTo(0, 0);
    }
  };

  const nachDerDrehung = (): void => {
    messen();
    window.setTimeout(messen, LAYOUT.orientationSettleMs);
  };

  messen();

  window.visualViewport?.addEventListener('resize', messen);
  window.visualViewport?.addEventListener('scroll', messen);
  window.addEventListener('resize', messen);
  window.addEventListener('orientationchange', nachDerDrehung);

  return () => {
    window.visualViewport?.removeEventListener('resize', messen);
    window.visualViewport?.removeEventListener('scroll', messen);
    window.removeEventListener('resize', messen);
    window.removeEventListener('orientationchange', nachDerDrehung);
  };
}
